import { UserAttributes } from '../models/login-register-model';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import {
  ApiRequest,
  ApiResponse,
  fetchDataFromAPITyped,
  isSuccessful,
} from './api-requests';
import { notificationService } from './notification-service';

export type UserInfoPayload = {
  user: UserAttributes;
};

export type PromotionPayload = {
  message?: string;
  error?: string;
};

export const getUserInfo = async (): Promise<
  ApiResponse<UserInfoPayload | null>
> => {
  const userInfoRequest: ApiRequest = {
    route: 'user/info',
    request: {
      method: 'GET',
      entity: 'user',
      body: undefined,
    },
  };
  const response = await fetchDataFromAPITyped<UserInfoPayload>(
    userInfoRequest
  );
  return response;
};

export const updateUserToServer = async (
  userInfo: Record<string, any>
): Promise<ApiResponse<UserAttributes | null>> => {
  const userInfoRequest: ApiRequest = {
    route: 'user/update',
    request: {
      method: 'PUT',
      entity: 'user',
      body: userInfo,
    },
  };
  const response = await fetchDataFromAPITyped<UserAttributes>(userInfoRequest);
  return response;
};

export const updateBillingAddressToServer = async (
  userInfo: Record<string, any>
): Promise<ApiResponse<UserAttributes | null>> => {
  const userInfoRequest: ApiRequest = {
    route: 'user/updateBillingAddress',
    request: {
      method: 'PUT',
      entity: 'user',
      body: userInfo,
    },
  };
  const response = await fetchDataFromAPITyped<UserAttributes>(userInfoRequest);
  return response;
};

//FUNCTION: getHostPlan
//PURPOSE: retrieve details about the user's active host plan
export const getHostPlan = async () => {
  try {
    const apiRequest: ApiRequest = {
      route: `user/getHostPlanInfo`,
      request: {
        method: 'GET',
        entity: 'userPlan',
        body: undefined,
      },
    };
    const driverPlan = await fetchDataFromAPITyped<UserPlanAssociation>(
      apiRequest
    );
    if (isSuccessful(driverPlan.status)) {
      return driverPlan.payload;
    }
    return null;
  } catch (error) {
    console.log('Unable to get driver plan', error);
  }
};

export const promoteUserToHost = async (userId: string, planId: number) => {
  try {
    const apiRequest: ApiRequest = {
      route: `user/${userId}/promoteToHost`,
      request: {
        method: 'POST',
        entity: 'user',
        body: { planId: planId },
      },
    };
    const promoteUserToHost = await fetchDataFromAPITyped<PromotionPayload>(apiRequest);
    if (isSuccessful(promoteUserToHost.status)) {
      return promoteUserToHost.payload;
    }
    return false;
  } catch (error) {
    notificationService.sendNotification('error', 'Unable to promote user to host');
    return false;
  }
};

export const promoteUserToPartner = async (userId: string, planId: number) => {
  try {
    const apiRequest: ApiRequest = {
      route: `user/${userId}/promoteToPartner`,
      request: {
        method: 'POST',
        entity: 'user',
        body: { planId: planId },
      },
    };
    const promoteUserToPartner = await fetchDataFromAPITyped<PromotionPayload>(apiRequest);
    if (isSuccessful(promoteUserToPartner.status)) {
      return promoteUserToPartner.payload;
    }
    return false;
  } catch (error) {
    notificationService.sendNotification('error', 'Unable to promote user to partner');
    return false;
  }
};

export const searchUsers = async (search: any) => {
  try {
    const apiRequest: ApiRequest = {
      route: `user/admin/search`,
      request: {
        method: 'POST',
        entity: 'user',
        body: { search },
      },
    };
    const searchUserNames = await fetchDataFromAPITyped<UserAttributes[]>(apiRequest);
    if (isSuccessful(searchUserNames.status)) {
      return searchUserNames.payload;
    }
    return false;
  } catch (error) {
    notificationService.sendNotification('error', 'Unable to search user names');
    return false;
  }
};

export const getUserProfile = async (userId: string) => {
  try {
    const apiRequest: ApiRequest = {
      route: `user/admin/profile`,
      request: {
        method: 'GET',
        entity: 'user',
        body: { userId: userId },
      },
    };
    const userProfile = await fetchDataFromAPITyped<UserAttributes>(apiRequest);
    if (isSuccessful(userProfile.status)) {
      return userProfile.payload;
    }
    return false;
  } catch (error) {
    notificationService.sendNotification('error', 'Unable to get user profile');
    return false;
  }
};